import * as React from 'react'
import { API } from '@getgreenline/homi-shared'
import { Card, Empty, Row, Col, Tag, PageHeader, message, Drawer } from 'antd'
import { useEffect, useState } from 'react'
import { browserHistory } from 'react-router'
import Title from 'antd/lib/typography/Title'
import ExternalSourceForm from './ExternalSourceForm'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'
import { PermissionDrawer } from './PermissionDrawer'
import { AdminExternalSourceStore } from './AdminExternalSourceStore'
import { parseErrorMsg } from '../../../utilities/helpers'
import { CopyToClipboardWithIcon } from '@/app/components/CopyToClipboard'
import { getSalesChannels } from '@/app/containers/Admin/PricingMenuChannels/api'
import { IAdminExternalSource, ICreateExternalSource } from './types'
import { ChannelLoadStatus, getChannelScopeDisplay } from './channelHelpers'

const adminExternalSourceStore = new AdminExternalSourceStore()

export const AdminExternalSources = () => {
  useEffect(() => {
    // Each loader fails independently so one rejection can't starve the others
    // (e.g. a permissions failure must not prevent channels from loading).
    getExternalSources()
    getAllExternalPermissions()
    getChannels()
  }, [])

  const [isPermissionDrawerOpen, setIsPermissionDrawerOpen] = useState(false)
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [channelsStatus, setChannelsStatus] = useState<ChannelLoadStatus>('loading')
  const permissionTitle = 'View / set permissions'

  const getExternalSources = async () => {
    setIsLoading(true)

    try {
      const externalSources = (await API.getExternalSources()) as IAdminExternalSource[]
      adminExternalSourceStore.setMappedExternalSources(externalSources)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading external sources')
    }

    setIsLoading(false)
  }

  const createExternalSource = async (externalSourceObject: ICreateExternalSource) => {
    try {
      const newExternalSource = await API.createExternalSource(externalSourceObject)
      message.success(`${newExternalSource.name} was successfully created`)

      await getExternalSources()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while creating an external source')
    }
  }

  const updateExternalSource = async (externalSourceObject: ICreateExternalSource) => {
    try {
      if (!adminExternalSourceStore.selectedExternalSource) {
        return message.error('Select an external source first')
      }
      const externalSourceId = adminExternalSourceStore.selectedExternalSource.id
      const newExternalSource = await API.updateExternalSourceById(
        externalSourceId,
        externalSourceObject,
      )

      message.success(`${newExternalSource.name} was successfully updated`)

      await getExternalSources()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while updating an external source')
    }
  }

  const getAllExternalPermissions = async () => {
    try {
      const allExternalPermissions = await API.getExternalPermissions()
      adminExternalSourceStore.setAvailablePermissions(allExternalPermissions)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading external permissions')
    }
  }

  const getChannels = async () => {
    setChannelsStatus('loading')

    try {
      const channels = await getSalesChannels()
      adminExternalSourceStore.setChannels(channels)
      setChannelsStatus('ready')
    } catch (error) {
      setChannelsStatus('error')
      message.error(parseErrorMsg(error) || 'An error occurred while loading sales channels')
    }
  }

  return (
    <div className='p-4'>
      <PageHeader
        style={{
          border: '1px solid rgb(235, 237, 240)',
        }}
        className='mb-4'
        onBack={() => browserHistory.goBack()}
        title='External Sources'
        subTitle='View and create external sources'
      />
      <Title>Create new external source</Title>
      <ExternalSourceForm
        labelCol={{ span: 7 }}
        formSpans={[12, 12]}
        adminExternalSourceStore={adminExternalSourceStore}
        shouldResetFields
        onSubmit={createExternalSource}
        channels={adminExternalSourceStore.channels}
        channelsStatus={channelsStatus}
        onRetryChannels={getChannels}
      />

      <Title>All External sources</Title>
      {isLoading ? (
        <Card loading={isLoading}></Card>
      ) : adminExternalSourceStore.externalSources.length === 0 ? (
        <Empty />
      ) : (
        <Row gutter={16} type='flex'>
          {adminExternalSourceStore.externalSources.map((externalSource) => {
            const { id, name, categoryName, apiKey, hexColor, isMarketplace, channelIds } =
              externalSource
            const channelScope = getChannelScopeDisplay(
              channelIds,
              adminExternalSourceStore.channels,
              channelsStatus,
            )

            return (
              <Col key={id} xxl={6} xl={6} lg={8} md={12} sm={24} xs={24}>
                <Card
                  title={
                    <div>
                      <div>{name}</div>
                      {hexColor === '' ? (
                        <Tag>No color selected</Tag>
                      ) : (
                        <Tag color={hexColor}>{hexColor}</Tag>
                      )}

                      {categoryName && <Tag>{categoryName}</Tag>}
                    </div>
                  }
                  className='mb-2'
                  extra={
                    <CopyToClipboardWithIcon
                      key={`${id}-copy`}
                      text={apiKey}
                      afterSuccess={() => message.success('API key copied to clipboard')}
                      afterFail={(e) => message.error(`Failed to copy API key: ${e.message}`)}
                    />
                  }
                >
                  <pre>
                    Name: {name}
                    <br />
                    API key: {apiKey}
                    <br />
                    Marketplace enabled: {isMarketplace.toString()}
                    <br />
                    Channels:{' '}
                    {channelScope.isUnscoped
                      ? 'Unscoped (all channels)'
                      : channelScope.isResolving
                      ? 'Loading channel names…'
                      : channelScope.loadFailed
                      ? 'Channel list failed to load'
                      : ''}
                  </pre>

                  {channelScope.loadFailed && (
                    <div className='mb-2' style={{ color: '#f5222d' }}>
                      Couldn&apos;t load channel names — this key stays scoped to its saved channels
                      either way.
                    </div>
                  )}

                  {channelScope.names.length > 0 && (
                    <div className='mb-2'>
                      {channelScope.names.map((channelName) => (
                        <Tag key={channelName}>{channelName}</Tag>
                      ))}
                    </div>
                  )}

                  <BlazeButton
                    buttonProps={{
                      className: 'mr-2',
                      onClick: () => {
                        adminExternalSourceStore.setSelectedExternalSource(externalSource)
                        setIsPermissionDrawerOpen(true)
                      },
                    }}
                    titleProps={{
                      title: permissionTitle,
                    }}
                  />

                  <BlazeButton
                    buttonProps={{
                      onClick: () => {
                        adminExternalSourceStore.setSelectedExternalSource(externalSource)
                        setIsEditDrawerOpen(true)
                      },
                    }}
                    titleProps={{
                      title: 'Edit',
                    }}
                  />
                </Card>
              </Col>
            )
          })}
        </Row>
      )}

      {adminExternalSourceStore.selectedExternalSource !== undefined && (
        <PermissionDrawer
          title={permissionTitle}
          isDrawerOpen={isPermissionDrawerOpen}
          setIsDrawerOpen={setIsPermissionDrawerOpen}
          adminExternalSourceStore={adminExternalSourceStore}
        />
      )}

      {isEditDrawerOpen && (
        <Drawer
          title={`Edit${
            adminExternalSourceStore.selectedExternalSource
              ? ` - ${adminExternalSourceStore.selectedExternalSource.name}`
              : ''
          }`}
          maskClosable
          placement='right'
          closable={false}
          width={350}
          onClose={() => {
            setIsEditDrawerOpen(false)
            adminExternalSourceStore.setSelectedExternalSource()
          }}
          visible={isEditDrawerOpen}
        >
          <ExternalSourceForm
            existingExternalSource={adminExternalSourceStore.selectedExternalSource}
            labelCol={{ span: 9 }}
            formSpans={[24]}
            onSubmit={updateExternalSource}
            channels={adminExternalSourceStore.channels}
            channelsStatus={channelsStatus}
            onRetryChannels={getChannels}
          />
        </Drawer>
      )}
    </div>
  )
}
